import { create } from 'zustand';
import type { Session, User } from '@supabase/supabase-js';
import type { OnboardResult } from '../api/client';
import { getPermissionsForRole, isValidRole } from '../lib/permissions';
import type { Permission } from '../lib/permissions';

export type OnboardingStatus = 'unknown' | 'needed' | 'done';

interface AuthState {
  session: Session | null;
  user: User | null;
  /** true while restoring the session from browser storage on first load */
  isLoading: boolean;
  /** Whether the current user has completed onboarding (has a profile + org) */
  onboardingStatus: OnboardingStatus;
  /** Organization context — populated after onboarding check succeeds */
  organizationId: string | null;
  organizationName: string | null;
  orgRole: string | null;
  /** Permissions derived from orgRole — for UX gating only */
  permissions: Permission[];
  /** Platform-level system admin flag — populated from /auth/onboard response */
  isSystemAdmin: boolean;

  // ── Internal setters (used by AuthProvider) ──
  setSession: (session: Session | null) => void;
  setLoading: (loading: boolean) => void;

  // ── Actions ──────────────────────────────────
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  verifyEmailOtp: (email: string, token: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  setOnboardingStatus: (status: OnboardingStatus) => void;
  /** Atomically stores org context and marks onboarding done. Call after onboard() succeeds. */
  setOrgContext: (result: OnboardResult) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  user: null,
  isLoading: true,
  onboardingStatus: 'unknown',
  organizationId: null,
  organizationName: null,
  orgRole: null,
  permissions: [],
  isSystemAdmin: false,

  setSession: (session) =>
    set({
      session,
      user: session?.user ?? null,
      onboardingStatus: session ? 'unknown' : 'done',
      organizationId: null,
      organizationName: null,
      orgRole: null,
      permissions: [],
      isSystemAdmin: false,
    }),

  setLoading: (loading) => set({ isLoading: loading }),

  signIn: async (email, password, rememberMe = false) => {
    // Dynamic import avoids a circular dep at module init time
    const { supabase, setRememberMePreference, clearSupabaseAuthSession } =
      await import('../lib/supabase');
    setRememberMePreference(rememberMe);
    if (!rememberMe) {
      clearSupabaseAuthSession('local');
    }
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (!data.session) {
      throw new Error('Sign-in completed, but no session was returned. Please try again.');
    }

    const prevUserId = get().user?.id;
    const nextUserId = data.session.user?.id;
    if (nextUserId && nextUserId !== prevUserId) {
      const { useAppStore } = await import('./useAppStore');
      useAppStore.getState().resetUserData();
    }

    set({
      session: data.session,
      user: data.session.user,
      onboardingStatus: 'unknown',
      organizationId: null,
      organizationName: null,
      orgRole: null,
      permissions: [],
    });
  },

  signUp: async (email, password) => {
    // Create the user and send OTP in one backend call so Supabase never
    // sends its own confirmation email — only our custom OTP goes out.
    const resp = await fetch('/api/auth/send-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      throw new Error((body as { error?: { message?: string } }).error?.message ?? 'Failed to send verification code');
    }
  },

  verifyEmailOtp: async (email, token) => {
    const resp = await fetch('/api/auth/verify-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code: token }),
    });
    const body = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error((body as { error?: { message?: string } }).error?.message ?? 'Invalid verification code');
    }
    // Email is verified — the caller navigates to /login so the user signs in normally.
  },

  signOut: async () => {
    const { supabase, clearRememberMePreference, clearSupabaseAuthSession } =
      await import('../lib/supabase');
    try {
      const { error } = await supabase.auth.signOut();
      if (error && import.meta.env.DEV) {
        console.warn(
          '[auth] Supabase signOut returned an error; clearing local session anyway.',
          error,
        );
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn('[auth] Supabase signOut failed; clearing local session anyway.', err);
      }
    } finally {
      clearSupabaseAuthSession();
      clearRememberMePreference();
    }
    set({
      session: null,
      user: null,
      onboardingStatus: 'done',
      organizationId: null,
      organizationName: null,
      orgRole: null,
      permissions: [],
      isSystemAdmin: false,
    });
  },

  resetPassword: async (email) => {
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  },

  updatePassword: async (newPassword) => {
    const { supabase } = await import('../lib/supabase');
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  },

  setOnboardingStatus: (status) => set({ onboardingStatus: status }),

  setOrgContext: (result) =>
    set({
      organizationId: result.organizationId,
      organizationName: result.organizationName,
      orgRole: result.role,
      permissions: isValidRole(result.role) ? getPermissionsForRole(result.role) : [],
      isSystemAdmin: result.isSystemAdmin ?? false,
      onboardingStatus: 'done',
    }),
}));
